<script setup lang="ts">
import { ref, computed, watch, defineEmits, defineProps } from 'vue'

const props = defineProps<{
  items: any[]
  perPageOptions?: number[]
  defaultPerPage?: number
}>()

const emit = defineEmits<{
  (e: 'update:page', page: number): void
  (e: 'update:paginated', data: any[]): void
}>()

// State
const currentPage = ref(1)
const perPage = ref(props.defaultPerPage || 10)
const perPageOptions = props.perPageOptions || [10, 25, 50, 100]

// Computed
const totalItems = computed(() => props.items?.length || 0)
const totalPages = computed(() => Math.ceil(totalItems.value / perPage.value))

const paginatedItems = computed(() => {
  const start = (currentPage.value - 1) * perPage.value
  const end = start + perPage.value
  return props.items.slice(start, end)
})

// Navigation
const goToPage = (page: number) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page
    emit('update:page', currentPage.value)
  }
}

const nextPage = () => goToPage(currentPage.value + 1)
const prevPage = () => goToPage(currentPage.value - 1)

// Emit whenever page or items change
watch([paginatedItems, perPage, currentPage], () => {
  emit('update:paginated', paginatedItems.value)
})

// Reset to page 1 when items length changes
watch(
  () => props.items.length,
  () => {
    currentPage.value = 1
    emit('update:paginated', paginatedItems.value)
  },
)

// 🧠 Smart adjustment when per-page limit changes
watch(perPage, (newLimit, oldLimit) => {
  const firstVisibleIndex = (currentPage.value - 1) * oldLimit
  const newPage = Math.floor(firstVisibleIndex / newLimit) + 1

  // Ensure page stays within valid range
  currentPage.value = Math.min(newPage, totalPages.value || 1)
  emit('update:paginated', paginatedItems.value)
})
</script>

<template>
  <div class="mt-4 flex flex-col items-center justify-between gap-4 text-sm text-gray-700 sm:flex-row">
    <!-- Info -->
    <div class="flex items-center gap-2">
      <span>Showing</span>
      <span class="font-medium">{{ (currentPage - 1) * perPage + 1 }}</span>
      -
      <span class="font-medium">{{ Math.min(currentPage * perPage, totalItems) }}</span>
      of
      <span class="font-medium">{{ totalItems }}</span>
      results
    </div>

    <!-- Controls -->
    <div class="flex items-center gap-3">
      <!-- Per Page -->
      <label class="flex items-center gap-1">
        <span>Rows per page:</span>
        <select v-model.number="perPage" class="min-w-[1rem] rounded border bg-white px-3 py-1 pr-8 text-sm">
          <option v-for="option in perPageOptions" :key="option" :value="option">
            {{ option }}
          </option>
        </select>
      </label>

      <!-- Prev -->
      <button
        @click="prevPage"
        :disabled="currentPage === 1"
        class="rounded border px-3 py-1.5 hover:bg-gray-50 disabled:opacity-50"
      >
        Previous
      </button>

      <span>Page {{ currentPage }} of {{ totalPages }}</span>

      <!-- Next -->
      <button
        @click="nextPage"
        :disabled="currentPage === totalPages"
        class="rounded border px-3 py-1.5 hover:bg-gray-50 disabled:opacity-50"
      >
        Next
      </button>
    </div>
  </div>
</template>
